/// <reference types="@types/google.maps" />
/*
* Usage:
*
const { initPlacesLibrary, initAutocomplete, getAddressComponentByType, place } = usePlaceAutocomplete()
const street = ref('')
const autoCompleteInput = ref()

onMounted(async () => {
    await initPlacesLibrary()
    initAutocomplete(autoCompleteInput.value as HTMLInputElement)
})

watch(place, (newVal) => {
    street.value = getAddressComponentByType('route')?.long_name
})
*
*/
import { ref, type Ref } from 'vue'
import { useRuntimeConfig } from '#imports'
import { setOptions, importLibrary } from "@googlemaps/js-api-loader"

export interface IUsePlaceAutocomplete {
    initPlacesLibrary(): Promise<void>,
    initAutocomplete (input: HTMLInputElement, types: string[]): void,
    getAddressComponentByType (type: string): AddressComponent | undefined,
    geocodeAddress (address: string): Promise<Place | null>,
    place: Ref<Place | null>
}

export interface AddressComponent {
    long_name?: string,
    short_name?: string,
    types?: string[]
}

export interface Place {
    address_components?: AddressComponent[]
}

// Maps api script needs a callback param which we don't need in composable context, so provide an empty function
if (import.meta.client) {
    window.emptyCallback = function () {}
}

export function usePlaceAutocomplete (): IUsePlaceAutocomplete {
    const { googleMapsPlacesApiKey } = useRuntimeConfig().public

    if (import.meta.client && typeof googleMapsPlacesApiKey === 'string' && googleMapsPlacesApiKey) {
        setOptions({ key: googleMapsPlacesApiKey })
    }

    // TODO: migrate to google.maps.places.PlaceAutocompleteElement (see TODO.md)
    const autoCompleteService = ref<google.maps.places.Autocomplete | null>(null)
    const place: Ref<Place | null> = ref(null)

    function initAutocomplete (input: HTMLInputElement, types: string[] = ['address']): void {
        if (!import.meta.client) return

        if (window.google?.maps?.places) {
            createAutocomplete(input, types)
        } else {
            window.addEventListener('google_maps_places_loaded', function () {
                createAutocomplete(input, types)
            })
        }
    }

    function createAutocomplete (input: HTMLInputElement, types: string[]): void {
        autoCompleteService.value = new google.maps.places.Autocomplete(input, {
            fields: ['address_components', 'geometry'],
            types
        })

        autoCompleteService.value.addListener('place_changed', onPlaceChanged)
    }

    function onPlaceChanged () {
        const changedPlace = autoCompleteService.value?.getPlace()

        if (!changedPlace?.address_components) {
            place.value = null
        } else {
            place.value = changedPlace
        }
    }

    async function initPlacesLibrary (): Promise<void> {
        if (!import.meta.client) return

        if (typeof googleMapsPlacesApiKey !== 'string' || !googleMapsPlacesApiKey) {
            return
        }

        if (window.google?.maps?.places) {
            return
        }

        const placesLib = await importLibrary('places')

        const event = new Event('google_maps_places_loaded')

        return new Promise((resolve, reject) => {
            if (placesLib != null) {
                window.dispatchEvent(event)
                resolve()
            } else {
                reject(new Error(`Google places failed to load`))
            }
        })
    }

    function getAddressComponentByType (type: string): AddressComponent | undefined {
        return place?.value?.address_components?.find((component) => {
            return component?.types?.includes(type)
        })
    }

    // Place Autocomplete (Legacy) does not always resolve address_components down to
    // subpremise/unit level for addresses it cannot fully interpolate (documented Google
    // limitation, e.g. "Piławska 7/5c" - A4G-3338). Geocoding the user's original input
    // parses it directly instead of looking up the selected prediction's stored data, and
    // can recover components Place Details left out.
    async function geocodeAddress (address: string): Promise<Place | null> {
        if (!import.meta.client) return null

        await importLibrary('geocoding')

        const geocoder = new google.maps.Geocoder()
        const { results } = await geocoder.geocode({ address })

        return results?.[0] ?? null
    }

    return {
        initPlacesLibrary,
        initAutocomplete,
        getAddressComponentByType,
        geocodeAddress,
        place
    }
}
